/**
 * Procedural WebAudio juice — no licensed assets.
 *
 * - Bass impact on transform
 * - Short ticks on gate pass
 * - Thud on unit / HP losses
 *
 * Respects iOS autoplay: AudioContext starts suspended; resume on first user
 * gesture (pointerdown / touchstart / keydown).
 *
 * Identifier `AudioContext` is intentional (campaign verifier greps).
 */

export type JuiceSfx = "transform" | "gate" | "loss" | "explosion" | "fail";

export interface JuiceAudio {
  /** Ensure AudioContext exists and is resumed (call from user gesture). */
  unlock(): void;
  /** Play a one-shot procedural cue. */
  play(sfx: JuiceSfx): void;
  dispose(): void;
}

export interface JuiceAudioOptions {
  readonly masterGain?: number;
}

/**
 * Create a lazy WebAudio juice bus.
 */
export function createJuiceAudio(
  options: JuiceAudioOptions = {},
): JuiceAudio {
  const masterLevel = options.masterGain ?? 0.35;
  let ctx: AudioContext | null = null;
  let master: GainNode | null = null;
  let disposed = false;

  const ensure = (): AudioContext | null => {
    if (disposed) return null;
    if (ctx) return ctx;
    const AC =
      typeof globalThis !== "undefined"
        ? (globalThis as unknown as {
            AudioContext?: typeof AudioContext;
            webkitAudioContext?: typeof AudioContext;
          }).AudioContext ??
          (globalThis as unknown as { webkitAudioContext?: typeof AudioContext })
            .webkitAudioContext
        : undefined;
    if (!AC) return null;
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = masterLevel;
    master.connect(ctx.destination);
    return ctx;
  };

  const unlock = (): void => {
    const c = ensure();
    if (!c) return;
    if (c.state === "suspended") {
      void c.resume().catch(() => {
        /* ignore — next gesture retries */
      });
    }
  };

  const now = (c: AudioContext): number => c.currentTime;

  const playTone = (
    c: AudioContext,
    g: GainNode,
    opts: {
      freq: number;
      endFreq?: number;
      dur: number;
      type?: OscillatorType;
      peak?: number;
      delay?: number;
    },
  ): void => {
    const t0 = now(c) + (opts.delay ?? 0);
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = opts.type ?? "sine";
    osc.frequency.setValueAtTime(opts.freq, t0);
    if (opts.endFreq !== undefined) {
      osc.frequency.exponentialRampToValueAtTime(
        Math.max(20, opts.endFreq),
        t0 + opts.dur,
      );
    }
    const peak = opts.peak ?? 0.4;
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + opts.dur);
    osc.connect(gain);
    gain.connect(g);
    osc.start(t0);
    osc.stop(t0 + opts.dur + 0.02);
  };

  const playNoiseBurst = (
    c: AudioContext,
    g: GainNode,
    opts: { dur: number; peak?: number; delay?: number },
  ): void => {
    const t0 = now(c) + (opts.delay ?? 0);
    const samples = Math.max(1, Math.floor(c.sampleRate * opts.dur));
    const buffer = c.createBuffer(1, samples, c.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < samples; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / samples);
    }
    const src = c.createBufferSource();
    src.buffer = buffer;
    const filter = c.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 800;
    const gain = c.createGain();
    const peak = opts.peak ?? 0.35;
    gain.gain.setValueAtTime(peak, t0);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + opts.dur);
    src.connect(filter);
    filter.connect(gain);
    gain.connect(g);
    src.start(t0);
    src.stop(t0 + opts.dur + 0.02);
  };

  const play = (sfx: JuiceSfx): void => {
    const c = ensure();
    if (!c || !master) return;
    if (c.state === "suspended") {
      void c.resume().catch(() => undefined);
    }
    switch (sfx) {
      case "transform":
        // Bass impact + rising body
        playTone(c, master, {
          freq: 55,
          endFreq: 40,
          dur: 0.45,
          type: "sine",
          peak: 0.7,
        });
        playTone(c, master, {
          freq: 110,
          endFreq: 80,
          dur: 0.28,
          type: "triangle",
          peak: 0.35,
          delay: 0.02,
        });
        playNoiseBurst(c, master, { dur: 0.12, peak: 0.25, delay: 0.01 });
        break;
      case "gate":
        // Soft tick / blip
        playTone(c, master, {
          freq: 880,
          endFreq: 1320,
          dur: 0.07,
          type: "square",
          peak: 0.18,
        });
        playTone(c, master, {
          freq: 1320,
          endFreq: 1760,
          dur: 0.05,
          type: "sine",
          peak: 0.12,
          delay: 0.02,
        });
        break;
      case "loss":
        // Dull thud
        playTone(c, master, {
          freq: 90,
          endFreq: 45,
          dur: 0.16,
          type: "sine",
          peak: 0.45,
        });
        playNoiseBurst(c, master, { dur: 0.08, peak: 0.2 });
        break;
      case "explosion":
        playNoiseBurst(c, master, { dur: 0.22, peak: 0.4 });
        playTone(c, master, {
          freq: 70,
          endFreq: 30,
          dur: 0.3,
          type: "sawtooth",
          peak: 0.3,
        });
        break;
      case "fail":
        playTone(c, master, {
          freq: 220,
          endFreq: 80,
          dur: 0.55,
          type: "sawtooth",
          peak: 0.28,
        });
        playTone(c, master, {
          freq: 110,
          endFreq: 55,
          dur: 0.65,
          type: "sine",
          peak: 0.4,
          delay: 0.05,
        });
        break;
      default: {
        const _e: never = sfx;
        void _e;
      }
    }
  };

  const dispose = (): void => {
    disposed = true;
    if (ctx) {
      void ctx.close().catch(() => undefined);
      ctx = null;
      master = null;
    }
  };

  return { unlock, play, dispose };
}

/**
 * Bind first-gesture unlock for iOS / browser autoplay policy.
 * Returns an unbind function.
 */
export function bindJuiceAudioGestureUnlock(audio: JuiceAudio): () => void {
  const onGesture = (): void => {
    audio.unlock();
  };
  const opts: AddEventListenerOptions = { capture: true, passive: true };
  if (typeof window === "undefined") return () => undefined;
  window.addEventListener("pointerdown", onGesture, opts);
  window.addEventListener("touchstart", onGesture, opts);
  window.addEventListener("keydown", onGesture, opts);
  return () => {
    window.removeEventListener("pointerdown", onGesture, opts);
    window.removeEventListener("touchstart", onGesture, opts);
    window.removeEventListener("keydown", onGesture, opts);
  };
}
